import {type ChecklistDoneForTemplateDto, SortOrder} from '@domain/hroc-checklists'
import {type TimeZone} from '@hconnect/common/types'
import {
  dateFormatter,
  type PaginationOptions,
  timeFormatter,
  selectableTableRowSx
} from '@hconnect/uikit'
import {Column, DataTable} from '@hconnect/uikit/src/lib2'
import {TFunction} from 'i18next'
import moment, {Moment} from 'moment-timezone'
import {useMemo} from 'react'

import {useWidgetTranslation} from '../hooks'

type Props = {
  data?: ChecklistDoneForTemplateDto[]
  isLoading?: boolean
  emptyMessage?: string
  selectedRowId?: string
  sortOrder: SortOrder
  paginationOptions: PaginationOptions
  onRowClick: (item: ChecklistDoneForTemplateDto) => void
  onSortChange: (columnKey: keyof ChecklistDoneForTemplateDto, sortOrder: SortOrder) => void
  timezone: TimeZone
}

export const CompletedChecklistsTable = ({
  data,
  isLoading,
  emptyMessage,
  selectedRowId,
  sortOrder,
  onRowClick,
  onSortChange,
  paginationOptions,
  timezone
}: Props) => {
  const {t} = useWidgetTranslation()
  const columns = useMemo(() => generateColumns({t, timezone}), [t, timezone])

  return (
    <DataTable<ChecklistDoneForTemplateDto>
      columns={columns}
      data={data}
      loading={isLoading}
      emptyMessage={emptyMessage}
      data-test-id={'completed-checklists-table'}
      onRowClick={(_, item) => onRowClick(item)}
      rowSx={({id}) => selectableTableRowSx(selectedRowId === id)}
      sortedBy="completedAt" // BE only support one column and doesn't require its name in the params
      sortDirection={sortOrder === SortOrder.ASCENDING ? 'asc' : 'desc'}
      onSort={(_, columnKey: keyof ChecklistDoneForTemplateDto) => {
        onSortChange(
          columnKey,
          sortOrder === SortOrder.ASCENDING ? SortOrder.DESCENDING : SortOrder.ASCENDING
        )
      }}
      paginationOptions={paginationOptions}
    />
  )
}

const generateColumns = ({
  t,
  timezone
}: {
  t: TFunction
  timezone: TimeZone
}): Column<ChecklistDoneForTemplateDto>[] => [
  {
    key: 'completedAt',
    label: t('label.dateTime'),
    customTemplate: ({completedAt}) => formatDateAndTime(moment.utc(completedAt).tz(timezone)),
    sortable: true
  },
  {
    key: 'preparedBy',
    label: t('label.preparedBy')
  }
]

// TODO: replace with new moment formatter with separator when it will be available
const formatDateAndTime = (date: Moment) => `${dateFormatter(date)} - ${timeFormatter(date)}`
